import {
  Button,
  Table,
  CellFormatOptions,
  ColumnTable,
  PaginationType,
  Modal,
  Icon,
  TextAreaInput,
} from "@octano/global-ui";
import { CellFormat } from "components/containers/CellFormat";
import { ErrorTableData } from "components/ErrorTableData";
import dayjs from "dayjs";
import { useTableData } from "hooks";
import { usePagination } from "hooks/usePagination";
import LeaveStateApproved from "./LeaveStateApproved";
import { useState } from "react";
import { LeaveApprovalModal } from "./LeaveApprovalsModal";
import { LeaveApprovalRequest, LeaveApprovals } from "../types";
import Loading from "components/Loading";
import { Col, Container, Row } from "reactstrap";

export interface LeaveApprovalsTableProps {
  data: LeaveApprovals[];
  onRefresh: () => void;
  handleLeaveRequest?: (
    leaveApprovalId: string,
    payload: { approved: boolean; comment?: string },
  ) => Promise<any>;
  handleAnnulRequest?: (
    leaveApprovalId: string,
    payload?: { comments?: string },
  ) => Promise<any>;
  showReport?: boolean;
  pagination?: PaginationType;
  isLoading?: boolean;
  onExport?: () => Promise<void> | void;
}

function capitalize(string: string) {
  if (!string) return "";
  return string
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function LeavesApprovalsTable({
  data: dataProp,
  onRefresh,
  handleLeaveRequest,
  handleAnnulRequest,
  showReport = false,
  pagination: externalPagination,
  isLoading,
  onExport,
}: LeaveApprovalsTableProps) {
  const [showAnnulModal, setShowAnnulModal] = useState(false);
  const [annulRecord, setAnnulRecord] = useState<LeaveApprovals | null>(null);
  const [annulComment, setAnnulComment] = useState("");
  const [isAnnulLoading, setIsAnnulLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportClick = async () => {
    if (onExport) {
      setIsExporting(true);
      try {
        await onExport();
      } finally {
        setIsExporting(false);
      }
    }
  };

  const confirmAnnul = async () => {
    if (annulRecord && handleAnnulRequest) {
      setIsAnnulLoading(true);
      try {
        await handleAnnulRequest(annulRecord.leaveApprovalId, {
          comments: annulComment,
        });
      } finally {
        setIsAnnulLoading(false);
        setShowAnnulModal(false);
        setAnnulRecord(null);
        setAnnulComment("");
      }
    }
  };

  const columnsDesktop: ColumnTable[] = [
    {
      columnName: "createdAt",
      headerText: <CellFormat>Fecha de solicitud</CellFormat>,
      cellFormat: ({ value }) => (
        <CellFormat>{dayjs(value).format("DD/MM/YYYY")}</CellFormat>
      ),
    },
    {
      columnName: "requestType",
      headerText: <CellFormat>Tipo de solicitud</CellFormat>,
      cellFormat: ({ value }) => <CellFormat>{capitalize(value)}</CellFormat>,
    },
    {
      columnName: "requestedDays",
      headerText: <CellFormat>Total de días hábiles</CellFormat>,
      cellFormat: ({ value, row }) => (
        <CellFormat>{row.isFullDay ? value : 0.5}</CellFormat>
      ),
    },
    {
      columnName: "comment",
      headerText: <CellFormat>Motivo</CellFormat>,
      cellFormat: ({ value }) => <CellFormat>{value}</CellFormat>,
    },
    {
      columnName: "requestBy",
      headerText: <CellFormat>Solicitado por</CellFormat>,
      cellFormat: ({ value }) => <CellFormat>{capitalize(value)}</CellFormat>,
    },
    {
      columnName: "approved",
      headerText: <CellFormat>Estado de Solicitud</CellFormat>,
      cellFormat: ({ value }) => {
        return (
          <CellFormat>
            <LeaveStateApproved approved={value} />
          </CellFormat>
        );
      },
    },
    {
      columnName: "doneBy",
      headerText: <CellFormat>Resuelto por</CellFormat>,
      cellFormat: ({ value }) => <CellFormat>{capitalize(value)}</CellFormat>,
    },
    {
      columnName: "id",
      headerText: <CellFormat>Acción</CellFormat>,
      width: "40px",
      tdClassName: "pr-2",
      cellFormat: (options: CellFormatOptions) => (
        <div className="d-flex justify-content-center align-items-center">
          <Button
            text="Revisar"
            size="sm"
            outlined
            onClick={() => {
              setRecordToEdit(options.row);
              setShowModal(true);
            }}
            className="cursor-pointer"
          />
        </div>
      ),
    },
  ];

  const { columns } = useTableData({
    columnsTable: columnsDesktop,
    columnsMobile: columnsDesktop,
    columnsDesktop,
  });

  const pagination = usePagination({
    totalItems: dataProp.length,
    limit: 5,
  });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [recordToEdit, setRecordToEdit] = useState<LeaveApprovalRequest | null>(
    null,
  );

  const data = dataProp;

  const toggleModal = () => {
    setShowModal(!showModal);
    setRecordToEdit(null);
  };

  return (
    <>
      {showReport && onExport && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "1rem",
          }}
        >
          <Button
            text="Descargar reporte"
            size="sm"
            onClick={handleExportClick}
            loading={isExporting}
          />
        </div>
      )}

      {isLoading ? (
        <Loading />
      ) : (
        <Table
          columns={columns}
          noResultsText={<ErrorTableData />}
          data={data}
          striped={false}
          pagination={externalPagination || pagination}
        />
      )}
      <LeaveApprovalModal
        show={showModal}
        toggleModal={toggleModal}
        onRefresh={onRefresh}
        recordToEdit={recordToEdit}
        handleLeaveRequest={handleLeaveRequest}
        onAnnul={
          handleAnnulRequest
            ? () => {
                setAnnulRecord(recordToEdit as unknown as LeaveApprovals);
                setShowModal(false);
                setShowAnnulModal(true);
              }
            : undefined
        }
      />
      <Modal
        isOpen={showAnnulModal}
        toggle={() => setShowAnnulModal(!showAnnulModal)}
        size="md"
        unmountOnClose
      >
        <Container fluid className="text-center">
          <Icon name="information" size={45} />
          <h1 className="text-dark fs-20 mt-3 mb-4">
            ¿Estás seguro de anular esta solicitud?
          </h1>
          <div className="mb-4 text-center text-muted">
            La solicitud dejará de ser válida. Esta acción no se puede deshacer.
          </div>

          {annulRecord && (
            <div className="p-3 rounded text-left">
              <Row className="mb-2">
                <Col xs={5}>
                  <strong>Solicitante:</strong>
                </Col>
                <Col xs={7}>{capitalize(annulRecord.requestBy)}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5}>
                  <strong>Tipo:</strong>
                </Col>
                <Col xs={7}>{capitalize(annulRecord.requestType)}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={5}>
                  <strong>Fecha inicio:</strong>
                </Col>
                <Col xs={7}>
                  {dayjs(annulRecord.startDate).format("DD/MM/YYYY")}
                </Col>
              </Row>
              {annulRecord.endDate && (
                <Row>
                  <Col xs={5}>
                    <strong>Fecha fin:</strong>
                  </Col>
                  <Col xs={7}>
                    {dayjs(annulRecord.endDate).format("DD/MM/YYYY")}
                  </Col>
                </Row>
              )}
            </div>
          )}

          <div className="mb-4 text-left">
            <TextAreaInput
              label="Comentario (opcional)"
              name="annulComment"
              value={annulComment}
              onChange={(e: any) => setAnnulComment(e.target.value)}
            />
          </div>

          <Row className="d-flex justify-content-around mt-5">
            <Col xs={12} md={6}>
              <Button
                outlined
                text="Cancelar"
                onClick={() => setShowAnnulModal(false)}
                fullwidth
              />
            </Col>
            <Col xs={12} md={6}>
              <Button
                text="Confirmar"
                onClick={confirmAnnul}
                fullwidth
                loading={isAnnulLoading}
              />
            </Col>
          </Row>
        </Container>
      </Modal>
    </>
  );
}
